import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { equipmentLabelStyle } from './styles';

interface DeliveryTubeProps {
  visible: boolean;
  isReacting: boolean;
  isPaused: boolean;
}

const DeliveryTube: React.FC<DeliveryTubeProps> = ({ visible, isReacting, isPaused }) => {
  const particleRef = useRef<THREE.InstancedMesh>(null);
  const groupRef = useRef<THREE.Group>(null);
  const scaleRef = useRef(0);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const particles = useMemo(() => Array.from({ length: 16 }, (_, i) => ({ t: i / 16 })), []);

  const curve = useMemo(() => {
    return new THREE.CatmullRomCurve3([
      new THREE.Vector3(-2.0, -0.30, 0),
      new THREE.Vector3(-1.3, 0.25, 0),
      new THREE.Vector3(-0.3, 0.10, 0),
      new THREE.Vector3(0.3, -0.50, 0),
      new THREE.Vector3(0.4, -1.05, 0),
    ]);
  }, []);

  useFrame(() => {
    if (groupRef.current) {
      scaleRef.current += ((visible ? 1 : 0) - scaleRef.current) * 0.06;
      groupRef.current.scale.setScalar(scaleRef.current);
    }
    if (!particleRef.current || !isReacting || isPaused) return;
    particles.forEach((p, i) => {
      p.t = (p.t + 0.006) % 1;
      const pos = curve.getPoint(p.t);
      dummy.position.copy(pos);
      dummy.scale.setScalar(0.045);
      dummy.updateMatrix();
      particleRef.current!.setMatrixAt(i, dummy.matrix);
    });
    particleRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group ref={groupRef} scale={0}>
      <mesh>
        <tubeGeometry args={[curve, 48, 0.022, 8, false]} />
        <meshPhysicalMaterial color="#90c0d0" transparent opacity={0.5} roughness={0.1} transmission={0.3} />
      </mesh>
      <instancedMesh ref={particleRef} args={[undefined, undefined, 16]}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshStandardMaterial color="#d0ecff" transparent opacity={0.7} emissive="#aaddff" emissiveIntensity={0.15} />
      </instancedMesh>
      {visible && (
        <Html position={[-0.3, 0.15, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={equipmentLabelStyle}>Delivery tube</div>
        </Html>
      )}
    </group>
  );
};

export default DeliveryTube;